import React, { useState } from 'react';
import type { DossierItem, DossierHole, ExhibitStatus } from '../../types/methodology';

const EXHIBIT_STATUS_LABEL: Record<ExhibitStatus, string> = {
  have_evidence: 'HAVE EVIDENCE',
  evidence_on_file_unused: 'ON FILE · UNUSED',
  missing: 'MISSING',
  not_assessed: 'NOT ASSESSED',
};

export interface MethodDossierSectionProps {
  strengths?: DossierItem[];
  weaknesses?: DossierItem[];
  holes?: DossierHole[];
}

export const MethodDossierSection: React.FC<MethodDossierSectionProps> = ({
  strengths: strengthsProp,
  weaknesses: weaknessesProp,
  holes: holesProp,
}) => {
  const [showDossier, setShowDossier] = useState(false);

  const strengths = strengthsProp ?? [];
  const weaknesses = weaknessesProp ?? [];
  const holes = holesProp ?? [];
  const dossierCount = strengths.length + weaknesses.length + holes.length;
  const dossierGroups: { title: string; items: DossierItem[] }[] = [
    { title: 'STRENGTHS', items: strengths },
    { title: 'WEAKNESSES', items: weaknesses },
    { title: 'HOLES', items: holes },
  ];

  return (
    <div className="pt-4 mt-4 border-t border-[var(--color-border)]">
      <button
        onClick={() => setShowDossier(!showDossier)}
        aria-expanded={showDossier}
        className="flex items-center gap-2 text-[11px] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors duration-150"
      >
        <span>{showDossier ? '▾' : '▸'}</span>
        <span>STRENGTHS · WEAKNESSES · HOLES · {dossierCount}</span>
      </button>

      {showDossier && (
        <div className="mt-3 space-y-3">
          {dossierCount === 0 ? (
            <p className="text-[12px] text-[var(--color-text-muted)]">
              No dossier items for this method.
            </p>
          ) : (
            dossierGroups.map(({ title, items }) =>
              items.length === 0 ? null : (
                <div key={title} className="space-y-2">
                  <div className="text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
                    {title}
                  </div>
                  {items.map((item, idx) => (
                    <div key={item.code} className="flex gap-3">
                      <span className="text-[12px] font-mono text-[var(--color-text-muted)] w-6 shrink-0">
                        {String(idx + 1).padStart(2, '0')}
                      </span>
                      <span className="flex-1 text-[12px] text-[var(--color-text-secondary)]">
                        {item.text}
                        {'exhibit_status' in item && (
                          <span className="ml-2 text-[10px] uppercase tracking-[0.08em] text-[var(--color-text-muted)]">
                            {EXHIBIT_STATUS_LABEL[(item as DossierHole).exhibit_status] ?? ''}
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              ),
            )
          )}
        </div>
      )}
    </div>
  );
};

export default MethodDossierSection;
